/**
 * VCoinCelebration — the "A V has been minted!" full-takeover moment.
 *
 * Renders a full-screen overlay when the coach has V-coin transactions
 * they haven't celebrated yet: a gold V-coin drops from the top, spins,
 * lands with a bounce and a confetti burst, the coin amount and reason
 * fade in, and the balance ticks up. Dismissing posts /vcoins/celebrated
 * so the moment never replays.
 *
 * Usage: <VCoinCelebration events={[...]} balance={n} onDismiss={fn} />
 *   events  – array of wallet transactions ({ key, amount, reason })
 *   balance – current coin balance (shown ticking up)
 *   onDismiss(keys) – called with all celebrated keys when closed
 *
 * Confetti is self-contained (tiny canvas implementation) so FTW gains
 * no new dependency.
 */
import React, { Component } from 'react';
import { arrayOf, func, number, object } from 'prop-types';
import css from './VCoinCelebration.css';

const REASON_COPY = {
  referral_network_monthly: 'You referred a Network member (monthly)',
  referral_network_annual: 'You referred a Network member (annual)',
  referral_verified_monthly: 'You referred a Verified Coach (monthly)',
  referral_verified_annual: 'You referred a Verified Coach (annual)',
  adjustment: 'Balance adjustment',
};

// ── Tiny confetti (no dependency) ──────────────────────
// ~80 gold/violet rectangles with simple physics, drawn on a canvas that
// overlays the celebration. Runs ~2.5s then stops.
function burstConfetti(canvas) {
  if (!canvas) return () => {};
  const ctx = canvas.getContext('2d');
  const W = (canvas.width = window.innerWidth);
  const H = (canvas.height = window.innerHeight);
  const COLORS = ['#FBBF24', '#F59E0B', '#8B5CF6', '#A78BFA', '#FFFFFF'];
  const N = 90;
  const parts = [];
  for (let i = 0; i < N; i++) {
    parts.push({
      x: W / 2 + (Math.random() - 0.5) * 120,
      y: H * 0.45,
      vx: (Math.random() - 0.5) * 14,
      vy: -6 - Math.random() * 10,
      w: 6 + Math.random() * 6,
      h: 8 + Math.random() * 8,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.3,
      color: COLORS[i % COLORS.length],
    });
  }
  let frame;
  const started = Date.now();
  const tick = () => {
    ctx.clearRect(0, 0, W, H);
    const elapsed = Date.now() - started;
    parts.forEach(p => {
      p.vy += 0.35; // gravity
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vr;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.globalAlpha = Math.max(0, 1 - elapsed / 2500);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      ctx.restore();
    });
    if (elapsed < 2500) {
      frame = window.requestAnimationFrame(tick);
    } else {
      ctx.clearRect(0, 0, W, H);
    }
  };
  frame = window.requestAnimationFrame(tick);
  return () => window.cancelAnimationFrame(frame);
}

class VCoinCelebration extends Component {
  constructor(props) {
    super(props);
    this.state = {
      index: 0, // which event is playing
      landed: false, // coin finished its drop
      shownBalance: null,
    };
    this.canvasRef = React.createRef();
    this.stopConfetti = null;
    this.handleLanded = this.handleLanded.bind(this);
    this.handleContinue = this.handleContinue.bind(this);
  }

  componentDidMount() {
    const { events, balance } = this.props;
    const total = events.reduce((s, e) => s + (e.amount || 0), 0);
    this.setState({ shownBalance: Math.max(0, (balance || 0) - total) });
    // The coin's CSS drop animation runs 1.1s; trigger the landing
    // effects right as it settles.
    this.landTimer = setTimeout(this.handleLanded, 1100);
  }

  componentWillUnmount() {
    clearTimeout(this.landTimer);
    clearTimeout(this.tickTimer);
    if (this.stopConfetti) this.stopConfetti();
  }

  handleLanded() {
    const { events, balance } = this.props;
    const { index } = this.state;
    this.setState({ landed: true });
    this.stopConfetti = burstConfetti(this.canvasRef.current);

    // Tick the balance counter up to its value after this event.
    const remaining = events.slice(index + 1).reduce((s, e) => s + (e.amount || 0), 0);
    const target = Math.max(0, (balance || 0) - remaining);
    const step = () => {
      this.setState(prev => {
        if (prev.shownBalance === null || prev.shownBalance >= target) return null;
        this.tickTimer = setTimeout(step, 80);
        return { shownBalance: prev.shownBalance + 1 };
      });
    };
    this.tickTimer = setTimeout(step, 300);
  }

  handleContinue() {
    const { events, onDismiss } = this.props;
    const { index } = this.state;
    if (index + 1 < events.length) {
      // Play the next minted coin.
      clearTimeout(this.landTimer);
      if (this.stopConfetti) this.stopConfetti();
      this.setState({ index: index + 1, landed: false });
      this.landTimer = setTimeout(this.handleLanded, 1100);
    } else {
      onDismiss(events.map(e => e.key));
    }
  }

  render() {
    const { events } = this.props;
    const { index, landed, shownBalance } = this.state;
    const event = events[index];
    if (!event) return null;

    const amount = event.amount || 0;
    const reasonText =
      REASON_COPY[event.reason] ||
      (event.meta && event.meta.referredEmail
        ? `Referral: ${event.meta.referredEmail}`
        : 'You earned V-coins');

    return (
      <div className={css.overlay} role="dialog" aria-label="V-coin earned">
        <canvas ref={this.canvasRef} className={css.confetti} />
        <div className={css.stage}>
          {/* The coin — pure CSS drop + spin + settle */}
          <div className={landed ? css.coinLanded : css.coinDropping} key={event.key}>
            <svg viewBox="0 0 120 120" className={css.coinSvg} aria-hidden="true">
              {/* outer coin */}
              <circle cx="60" cy="60" r="56" fill="#FBBF24" />
              <circle cx="60" cy="60" r="56" fill="none" stroke="#B45309" strokeWidth="3" />
              {/* inner ring */}
              <circle cx="60" cy="60" r="46" fill="none" stroke="#D97706" strokeWidth="2" strokeDasharray="4 3" />
              {/* highlight */}
              <ellipse cx="44" cy="38" rx="20" ry="12" fill="#FDE68A" opacity="0.8" />
              {/* The V with the double crossbar — the currency glyph */}
              <text
                x="60"
                y="82"
                textAnchor="middle"
                fontFamily="Georgia, 'Times New Roman', serif"
                fontWeight="bold"
                fontSize="64"
                fill="#7C2D12"
              >
                V
              </text>
              <line x1="38" y1="50" x2="82" y2="50" stroke="#7C2D12" strokeWidth="4" />
              <line x1="41" y1="59" x2="79" y2="59" stroke="#7C2D12" strokeWidth="4" />
            </svg>
          </div>

          <div className={landed ? css.textVisible : css.textHidden}>
            <h1 className={css.headline}>
              {amount === 1 ? 'A V has been minted!' : `${amount} Vs have been minted!`}
            </h1>
            <p className={css.reason}>{reasonText}</p>
            <p className={css.balance}>
              Your wallet: <span className={css.balanceNumber}>{shownBalance === null ? '—' : shownBalance} V</span>
            </p>
            <p className={css.hint}>10 V = 1 free month of Network · 20 V = 1 free month of Verified</p>
            <button type="button" className={css.continueButton} onClick={this.handleContinue}>
              {index + 1 < events.length ? 'Next coin →' : 'Collect'}
            </button>
            {events.length > 1 ? (
              <p className={css.queue}>
                Coin {index + 1} of {events.length}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
}

VCoinCelebration.defaultProps = {
  events: [],
  balance: 0,
};

VCoinCelebration.propTypes = {
  events: arrayOf(object),
  balance: number,
  onDismiss: func.isRequired,
};

export default VCoinCelebration;
